import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createHeadingNode } from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import FormatListBulleted from "@mui/icons-material/FormatListBulleted";
import FormatListNumbered from "@mui/icons-material/FormatListNumbered";
import Subject from "@mui/icons-material/Subject";
import { Typography } from "@mui/material";
import { $createParagraphNode, $getSelection, $isRangeSelection } from "lexical";
import { useCallback, useState } from "react";
import { ToolbarDropDown } from "./toolbar-drop-down";
import { ToolbarIconButton } from "./toolbar-icon-button";

import type { ToolbarProps } from "./props";
import type { HeadingTagType } from "@lexical/rich-text";
import type { FC } from "react";

export const BlockFormatDropDown: FC<
  ToolbarProps & { blockType: keyof typeof blockTypeToBlockInfo }
> = ({ disabled, blockType, mode }) => {
  const [editor] = useLexicalComposerContext();
  const [open, setOpen] = useState(false);

  const formatParagraph = useCallback((): void => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) $setBlocksType(selection, () => $createParagraphNode());
    });
  }, [editor]);

  const formatHeading = useCallback(
    (headingSize: HeadingTagType): void => {
      if (blockType !== headingSize) {
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection))
            $setBlocksType(selection, () => $createHeadingNode(headingSize));
        });
      }
    },
    [blockType, editor],
  );

  const formatBulletList = useCallback((): void => {
    editor.dispatchCommand(
      blockType === "bullet" ? REMOVE_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND,
      undefined,
    );
  }, [editor, blockType]);

  const formatNumberedList = useCallback((): void => {
    editor.dispatchCommand(
      blockType === "number" ? REMOVE_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND,
      undefined,
    );
  }, [editor, blockType]);

  return (
    <ToolbarDropDown
      data-analytics-id="lexical-toolbar-dropdown-block-format"
      disabled={disabled}
      icon={blockTypeToBlockInfo[blockType].icon}
      heading={blockTypeToBlockInfo[blockType].name}
      open={open}
      setOpen={setOpen}
      mode={mode}
    >
      {order.map((block) => (
        <ToolbarIconButton
          data-analytics-id={`lexical-toolbar-block-format-${block}`}
          key={block}
          disabled={disabled}
          label={blockTypeToBlockInfo[block].name}
          icon={blockTypeToBlockInfo[block].icon}
          data-testid={`toolbar-button-${block}`}
          aria-label={`format-${block}`}
          tabIndex={-1}
          onClick={() => {
            setOpen(false);
            switch (block) {
              case "paragraph":
                formatParagraph();
                break;
              case "number":
                formatNumberedList();
                break;
              case "bullet":
                formatBulletList();
                break;
              default:
                formatHeading(block);
                break;
            }
          }}
        />
      ))}
    </ToolbarDropDown>
  );
};

export const blockTypeToBlockInfo = {
  bullet: { name: "Bulleted List", icon: <FormatListBulleted /> },
  h1: { name: "Heading 1", icon: <Typography>H1</Typography> },
  h2: { name: "Heading 2", icon: <Typography>H2</Typography> },
  h3: { name: "Heading 3", icon: <Typography>H3</Typography> },
  h4: { name: "Heading 4", icon: <Typography>H4</Typography> },
  number: { name: "Ordered List", icon: <FormatListNumbered /> },
  paragraph: { name: "Normal", icon: <Subject /> },
};

const order: (keyof typeof blockTypeToBlockInfo)[] = [
  "paragraph",
  "h1",
  "h2",
  "h3",
  "h4",
  "number",
  "bullet",
];
